import { PLAYER_PORTRAIT_PATH, UI_CHROME_ART, skillArtPath } from '../../content/assets/uiArt';
import { protagonist } from '../../content/protagonist/protagonist';
import { resolveEnemy } from '../../content/enemies/enemies';
import { getOwnedSkills } from '../../content/skills/skills';
import { skillHitPercent } from '../../domain/combat/accuracy';
import { computeModifiedSkillDamage } from '../../domain/combat/damage';
import { fleeChanceLabel, fleeChancePercent } from '../../domain/combat/flee';
import { activeStatusDescription, activeStatusLabel, type StatusId } from '../../domain/combat/statuses';
import { elementLabel, type Element } from '../../domain/game/elements';
import { useGameStore } from '../../store/gameStore';
import { GothicFrame, OrnamentalDivider } from '../chrome/GothicChrome';
import { SvgIcon } from '../chrome/SvgIcon';
import type { UiSymbolName } from '../../content/assets/uiSymbols';
import { selectCombat, selectDungeon, selectPlayer } from '../../store/selectors';
import { InventoryPanel } from '../hud/InventoryPanel';

const ELEMENT_SYMBOLS: Record<Element, UiSymbolName> = {
  physical: 'element-physical',
  wind: 'element-wind',
  fire: 'element-fire',
  water: 'element-water',
  lightning: 'element-lightning',
  light: 'element-light',
  earth: 'element-earth',
  dark: 'element-dark',
};

const STATUS_SYMBOLS: Record<StatusId, UiSymbolName> = {
  stun: 'status-stun',
  freeze: 'status-freeze',
  paralysis: 'status-paralysis',
  poison: 'status-poison',
  burn: 'status-burn',
  weaken: 'status-weaken',
  fear: 'status-fear',
  invulnerable: 'status-invulnerable',
  elemental_immunity: 'status-elemental-immunity',
  power_up: 'status-power-up',
};

export function CombatPanel() {
  const combat = useGameStore(selectCombat);
  const player = useGameStore(selectPlayer);
  const dungeon = useGameStore(selectDungeon);
  const useSkill = useGameStore((state) => state.useSkill);
  const finishCombat = useGameStore((state) => state.finishCombat);
  const attemptFlee = useGameStore((state) => state.attemptFlee);
  const reset = useGameStore((state) => state.reset);

  const ownedSkills = getOwnedSkills(player.skillIds);
  const enemyDefinition = combat ? resolveEnemy(combat.enemyId) : null;
  const defeated = Boolean(combat && combat.enemyHp <= 0);
  const playerDown = Boolean(combat?.playerDefeated) || !player.alive;
  const enemyHealthRatio = combat && combat.enemyMaxHp > 0
    ? Math.max(0, Math.min(1, combat.enemyHp / combat.enemyMaxHp))
    : 0;
  const manaRatio = player.maxMana > 0 ? Math.max(0, player.mana / player.maxMana) : 0;

  if (playerDown) {
    return (
      <div className="combat-result-overlay">
        <section className="combat-result-card gothic-panel">
          <GothicFrame />
          <small>HÀNH TRÌNH KẾT THÚC</small>
          <h1>Bạn đã ngã xuống</h1>
          <p>Chỉ một đòn đánh trúng cũng đủ kết thúc sinh mệnh của Kẻ Đi Trước.</p>
          <div className="combat-log combat-log-result">
            {combat?.log.slice(-5).map((entry, index) => <p key={`${index}-${entry}`}>{entry}</p>)}
          </div>
          <button className="primary-button danger" type="button" onClick={reset}>Chơi lại từ đầu</button>
        </section>
      </div>
    );
  }

  return (
    <div className="combat-interface">
      <header className="combat-encounter-heading">
        <small>TẦNG {dungeon.floorNumber.toString().padStart(2, '0')} · CHẠM TRÁN</small>
        <strong>{defeated ? 'KẺ ĐỊCH ĐÃ BỊ HẠ' : 'BẠN LUÔN HÀNH ĐỘNG TRƯỚC'}</strong>
      </header>

      <section className="enemy-dossier gothic-panel">
        <GothicFrame />
        <small>KẺ ĐỐI ĐẦU</small>
        <h1>{combat?.enemyName ?? 'Kẻ địch vô danh'}</h1>
        <b>{enemyDefinition?.faction.toUpperCase() ?? 'CHƯA RÕ'}</b>
        <p>{combat?.enemyDescription}</p>
        <OrnamentalDivider />

        {combat ? (
          <div className="enemy-health">
            <div className="resource-label">
              <span>THỂ LỰC</span>
              <strong>{combat.enemyHp} / {combat.enemyMaxHp}</strong>
            </div>
            <div className="resource-track health"><i style={{ width: `${enemyHealthRatio * 100}%` }} /></div>
          </div>
        ) : null}

        {combat && combat.enemyStatuses.length > 0 ? (
          <div className="status-list">
            {combat.enemyStatuses.map((status) => (
              <span key={`${status.id}-${status.element ?? 'none'}`} className="status-chip enemy">
                <SvgIcon name={STATUS_SYMBOLS[status.id]} />
                <span>
                  <strong>{activeStatusLabel(status)}</strong>
                  <small>{activeStatusDescription(status)} · {status.remainingTurns} hiệp</small>
                </span>
              </span>
            ))}
          </div>
        ) : null}

        <details className="combat-log-drawer">
          <summary><SvgIcon name="action-combat-log" /> Quan sát diễn biến</summary>
          <div className="combat-log">
            {combat?.log.slice(-8).map((entry, index) => <p key={`${index}-${entry}`}>{entry}</p>)}
          </div>
        </details>
      </section>

      <section className="player-combat-card gothic-panel">
        <GothicFrame />
        <div className="combat-portrait" aria-hidden="true">
          <img className="combat-portrait-art" src={PLAYER_PORTRAIT_PATH} alt="" />
          <img className="combat-portrait-crest" src={UI_CHROME_ART.playerCombatCrest} alt="" />
        </div>
        <div className="combat-player-copy">
          <small>{protagonist.title.toUpperCase()}</small>
          <strong>{protagonist.name}</strong>
          <div className="compact-resource">
            <span>HP</span><b>{player.hp}/{player.maxHp}</b>
            <i><em style={{ width: player.alive ? '100%' : '0%' }} /></i>
          </div>
          <div className="compact-resource mana">
            <span>MP</span><b>{player.mana}/{player.maxMana}</b>
            <i><em style={{ width: `${manaRatio * 100}%` }} /></i>
          </div>
          {combat && combat.playerStatuses.length > 0 ? (
            <div className="player-status-inline">
              {combat.playerStatuses.map((status) => (
                <span key={`${status.id}-${status.element ?? 'none'}`} title={activeStatusDescription(status)}>
                  <SvgIcon name={STATUS_SYMBOLS[status.id]} />
                  {activeStatusLabel(status)} · {status.remainingTurns}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <details className="combat-item-drawer">
          <summary><SvgIcon name="action-items" /> Vật phẩm</summary>
          <InventoryPanel compact />
        </details>
      </section>

      <section className="combat-command-deck">
        {defeated ? (
          <button className="victory-button gothic-panel" type="button" onClick={finishCombat}>
            <GothicFrame />
            <small>CHIẾN THẮNG</small>
            <strong>Nhận thưởng & tiếp tục</strong>
          </button>
        ) : (
          <div className="skill-hand">
            {ownedSkills.map((skill) => {
              const disabled = player.mana < skill.manaCost || !player.alive;
              const hitPct = skillHitPercent(skill.accuracy, player.stats);
              const artPath = skillArtPath(skill.id);
              const damage = combat && enemyDefinition
                ? computeModifiedSkillDamage(
                    skill.element,
                    skill.power,
                    player.stats,
                    enemyDefinition.weak,
                    enemyDefinition.resist,
                    enemyDefinition.immune,
                    combat.playerStatuses,
                    combat.enemyStatuses,
                  )
                : null;

              return (
                <button
                  key={skill.id}
                  type="button"
                  className={`skill-card element-${skill.element}`}
                  disabled={disabled}
                  onClick={() => useSkill(skill.id)}
                  aria-label={`${skill.name}, ${hitPct}% chính xác, ${skill.manaCost} mana`}
                >
                  <GothicFrame variant="card" />
                  <span className="skill-card-top">
                    <SvgIcon className="skill-element-icon" name={ELEMENT_SYMBOLS[skill.element]} />
                    <strong>{skill.name}</strong>
                    <b>{skill.manaCost}</b>
                  </span>
                  <span className="skill-art" aria-hidden="true">
                    {artPath ? (
                      <img src={artPath} alt="" />
                    ) : (
                      <SvgIcon className="skill-art-fallback" name={ELEMENT_SYMBOLS[skill.element]} />
                    )}
                  </span>
                  <span className="skill-card-meta">
                    <small>{elementLabel(skill.element)} · Chính xác {hitPct}%</small>
                    <strong>
                      {damage?.blockedByStatus ? 'Sát thương 0' : `Sát thương ${damage?.finalDamage ?? '—'}`}
                    </strong>
                    {damage ? (
                      <span title={`Gốc ${skill.power} + ${damage.scaling}; thuộc tính ×${damage.multiplier}; buff ×${damage.powerUpMultiplier}; debuff ×${damage.receivedDamageMultiplier}`}>
                        Chi tiết sát thương ⓘ
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {!defeated ? (
        <aside className="flee-panel gothic-panel">
          <GothicFrame />
          <SvgIcon className="flee-icon" name="action-flee" />
          <strong>CHẠY TRỐN</strong>
          <small>Tỉ lệ thành công</small>
          <b>{fleeChancePercent(player.stats)}%</b>
          <p>Mất toàn bộ vật phẩm dùng trong trận khi thất bại.</p>
          <button type="button" onClick={attemptFlee}>{fleeChanceLabel(player.stats)}</button>
        </aside>
      ) : null}
    </div>
  );
}
